/**
 * §21.3 step 5 / §30.1 — the answer idempotency key.
 *
 * One key per round per device, reused by every retry of that submission, so a
 * resend after a dropped response cannot score twice. It lives in its own module
 * because it has to fit the 64 characters `submitAnswerSchema` accepts, and that
 * limit is worth a test: a key that overruns it fails validation at the edge, and
 * the player's answer is lost with a generic error.
 *
 * §18.5 — Local Event Mode serves players over `http://<lan-ip>`, which is not a
 * secure context. `crypto.randomUUID` and `crypto.subtle` do not exist there, so
 * neither may be used on a player device. `crypto.getRandomValues` is available
 * in every context and is what this uses.
 */

export const MAX_IDEMPOTENCY_KEY_LENGTH = 64;

/** 16 hex characters of randomness: unique per device for one round. */
export function randomSuffix(): string {
  const globalCrypto = typeof crypto === "undefined" ? undefined : crypto;

  if (globalCrypto?.getRandomValues) {
    const bytes = new Uint8Array(8);
    globalCrypto.getRandomValues(bytes);
    return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  }

  // Last resort. Only the idempotency key depends on this, and a collision would
  // need two devices to draw the same value on the same round in the same
  // millisecond — losing one answer, never scoring one twice.
  return Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
}

export function buildIdempotencyKey(roundId: string, random: () => string = randomSuffix): string {
  // A round id is already 36 characters, so the device half is a short random
  // suffix rather than a second UUID.
  const suffix = random()
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(0, 16);
  return `${roundId}:${suffix}`;
}
